import { Component, OnInit, OnDestroy, Inject, ViewChild, ViewContainerRef, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConnectionService } from 'ng-connection-service';
import amplitude from "amplitude-js";
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import * as moment from "moment";
import { OtpModalService } from './shared/modal/otp-modal.service';
import { NetworkService } from './network/network.service';
import { DataService } from './dashboard/_services/data.service';
import { TabSyncService } from './_services/tab-sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'blueride';
  isConnected: any = false;
  connectionChanged = false;
  noInternetConnection: boolean;
  poorNetwork = false;
  connectionBars = 0; // Number of bars to display (1 to 4)
  connectionSpeed = ''; // Speed in Mbps
  networkInfoApiSupported:boolean = true;
  isIframe = false;
  loginDisplay = false;
  showLiveHelp = false;
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('otpContainer', { read: ViewContainerRef, static: false }) containerRef: ViewContainerRef;

  constructor(private translate: TranslateService,
    private connectionService: ConnectionService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private otpModalService: OtpModalService,
    // the networService is injected here to act as an entry point for the singleton
    private networkService: NetworkService ,
    // injected here so every tab starts listening for session changes made in other tabs
    private tabSyncService: TabSyncService,
    private renderer: Renderer2,
    private dataService: DataService) {
    const lang = localStorage.getItem("lang");
    localStorage.setItem("lang", lang ? lang : 'en');
    translate.setDefaultLang(lang ? lang : 'en');
    document.dir = lang == 'en' ? 'ltr' : 'rtl';

    // this.connectionService.monitor().subscribe(isConnected => {
    //   this.connectionChanged = true;
    //   if (isConnected.hasNetworkConnection) {
    //     this.noInternetConnection = false;
    //     if (localStorage.getItem('connectionStatus')) {
    //       localStorage.removeItem('connectionStatus')
    //       window.location.reload();
    //     }

    //     this.checkNetworkQuality(); // Check for poor network quality

    //     setTimeout(() => {
    //       this.connectionChanged = false;
    //     }, (3 * 365 * 24 * 60 * 60 * 10));
    //   }
    //   else {
    //     this.noInternetConnection = true;
    //     localStorage.setItem('connectionStatus', 'disconnected')
    //   }
    //    // Start monitoring poor network quality
    // this.checkNetworkQuality();
    // })

    // initialize amplitude with options
    amplitude.getInstance().init("c92cb9e976fcbff4cc93fc65958402c");
    this.updateTimeZone();
  }

  ngOnInit(): void {
    this.initLiveHelpVisibility();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });
    
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      })

        // Start monitoring connection changes
    this.monitorNetworkConnection();

    // Monitor network quality changes
    this.monitorNetworkQuality();
  }

  ngAfterViewInit() {
    // Ensure that the containerRef is set after the view is initialized
    if (this.containerRef) {
      this.otpModalService.setContainer(this.containerRef);
    }
    // Freshchat-widget draggable
    // this.makeChatDraggable();
    // Freshchat-widget hide for specific roles
    // this.hideFreshChatForRoles();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

 
  /**
   * Monitors network connection status (online/offline).
   */
  monitorNetworkConnection() {
    const updateConnectionStatus = () => {
      const isOnline = navigator.onLine;

      this.connectionChanged = true;
      if (isOnline) {
        this.noInternetConnection = false;

        // Remove 'connectionStatus' and reload if it was previously set
        if (localStorage.getItem('connectionStatus')) {
          localStorage.removeItem('connectionStatus');
          window.location.reload();
        }
      } else {
        this.noInternetConnection = true;
        localStorage.setItem('connectionStatus', 'disconnected');
      }
    };

    // Listen for online/offline events
    window.addEventListener('online', updateConnectionStatus);
    window.addEventListener('offline', updateConnectionStatus);

    // Initial check
    updateConnectionStatus();
  }

   /**
   * Monitors network quality and updates status based on effectiveType.
   */

  monitorNetworkQuality() {
    const connection = (navigator as any).connection as {
      effectiveType?: string;
      downlink?: number;
      addEventListener?: (type: string, listener: EventListener) => void;
    };

    if (connection) {
      this.networkInfoApiSupported = true;
      const updateQuality = () => {
        const { downlink } = connection;

        // Update connection speed and quality
        this.connectionSpeed = `${downlink || 0} Mbps`;
        if (downlink <= 2) {
          this.poorNetwork = true;
          this.connectionBars = 1; // Poor connection: 1 bar
        } else if (downlink > 2 && downlink <= 4) {
          this.poorNetwork = false;
          this.connectionBars = 2; // Moderate connection: 2 bars
        } else if (downlink > 4) {
          this.poorNetwork = false;
          this.connectionBars = 3; // Excellent connection: 3 bars
        }

        this.connectionChanged = true;
      };

      // Initial quality check
      updateQuality();

      // Listen for network quality changes
      if (connection.addEventListener) {
        connection.addEventListener('change', updateQuality);
      }
    } else {
      this.networkInfoApiSupported = false;
      console.warn('Network Information API is not supported in this browser.');
    }
  }
  //  monitorNetworkQuality() {
  //   console.log('quality check')
  //   const connection = (navigator as any).connection as {
  //     effectiveType?: string;
  //     downlink?: number;
  //     addEventListener?: (type: string, listener: EventListener) => void;
  //   };

  //   if (connection) {
  //     const updateQuality = () => {
  //       console.log('qual = ' , connection);
        
  //       const { effectiveType, downlink } = connection;

  //       // Define poor network conditions
  //       if (
  //         downlink < 2
  //         // Downlink speed below 2 Mbps
  //         ) {
  //         this.poorNetwork = true;
  //       } else {
  //         this.poorNetwork = false;
  //       }

  //       this.connectionChanged = true;
  //       // setTimeout(() => {
  //       //   this.connectionChanged = false;
  //       // }, 3000); // 3 seconds
  //     };

  //     // Initial quality check
  //     updateQuality();

  //     // Listen for network quality changes
  //     if (connection.addEventListener) {
  //       connection.addEventListener('change', updateQuality);
  //     }
  //   } else {
  //     console.warn('Network Information API is not supported in this browser.');
  //   }
  // }

  // checkNetworkQuality() {
  //   const connection = (navigator as any).connection || navigator['mozConnection'] || navigator['webkitConnection'];

  //   if (connection) {
  //     const updateQuality = () => {
  //       const { effectiveType, downlink } = connection;
  //       if (
  //         effectiveType === 'slow-2g' ||
  //         effectiveType === '2g' ||
  //         effectiveType === '3g' ||
  //         (downlink && downlink < 2) // Downlink speed below 2 Mbps
  //       ) {
  //         this.poorNetwork = true;
  //       } else {
  //         this.poorNetwork = false;
  //       }
  //     };

  //     updateQuality(); // Initial check
  //     connection.addEventListener('change', updateQuality); // Listen for changes
  //   }
  // }
  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }


  updateTimeZone() {
    let timeZoneOffset;
    let timeZone;
    let saudiTimeZoneOffset = 3;
    let relativeOffset;
    timeZoneOffset = new Date().getTimezoneOffset() / -60;
    localStorage.setItem("timeZoneOffset", timeZoneOffset);
    relativeOffset = timeZoneOffset - saudiTimeZoneOffset;
    localStorage.setItem("relativeOfssetFromSaudi", relativeOffset);
    timeZone = moment.tz.guess();
    localStorage.setItem("timeZone", timeZone);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  //Freshchat-widget dragging method
  // makeChatDraggable() {
  //   setTimeout(() => {
  //     let chatWidget = document.getElementById("fc_frame");
  //     if (!chatWidget) return;
  
  //     let dragHandle = document.createElement("div");
  //     dragHandle.style.position = "absolute";
  //     dragHandle.style.left = window.innerWidth <= 768 ? "47%" : "40%";
  //     dragHandle.style.bottom = "7px";
  //     dragHandle.style.width = "15px";
  //     dragHandle.style.height = "15px";
  //     dragHandle.style.cursor = "grab";
  //     dragHandle.style.borderRadius = "5px";
  //     dragHandle.style.background = "url('/assets/icons/freshchat/drag.svg') no-repeat center";
  //     dragHandle.style.backgroundSize = "contain";
  
  //     chatWidget.appendChild(dragHandle);
  
  //     let isDragging = false, offsetX = 0, offsetY = 0;
  //     const isRTL = document.documentElement.dir === "rtl"; // Detect RTL mode
  
  //     let initialLeft = chatWidget.style.left;
  //     let initialRight = chatWidget.style.right;
  //     let initialTop = chatWidget.style.top;
  
  //     this.toggleFrechChatDrag(chatWidget, dragHandle, isDragging, isRTL, initialLeft, initialRight, initialTop);
  
  //     const moveChat = (clientX: number, clientY: number) => {
  //       let newX = clientX - offsetX;
  //       let newY = clientY - offsetY;
  
  //       // Ensure chat stays within visible bounds
  //       newX = Math.max(0, Math.min(newX, window.innerWidth - chatWidget.offsetWidth));
  //       newY = Math.max(0, Math.min(newY, window.innerHeight - chatWidget.offsetHeight));
  
  //       if (isRTL) {
  //         chatWidget.style.left = "auto"; // Reset left
  //         chatWidget.style.right = `${window.innerWidth - (newX + chatWidget.offsetWidth)}px`;
  //       } else {
  //         chatWidget.style.right = "auto"; // Reset right
  //         chatWidget.style.left = `${newX}px`;
  //       }
  
  //       chatWidget.style.top = `${newY}px`;
  //     };
  
  //     // --- 🖱 Desktop Dragging (Mouse) ---
  //     const onMouseMove = (e: MouseEvent) => {
  //       if (!isDragging) return;
  //       requestAnimationFrame(() => moveChat(e.clientX, e.clientY));
  //     };
  
  //     dragHandle.addEventListener("mousedown", (e: MouseEvent) => {
  //       isDragging = true;
  //       offsetX = e.clientX - chatWidget.getBoundingClientRect().left;
  //       offsetY = e.clientY - chatWidget.getBoundingClientRect().top;
  //       dragHandle.style.cursor = "grabbing";
  
  //       document.addEventListener("mousemove", onMouseMove);
  //       document.addEventListener("mouseup", () => {
  //         isDragging = false;
  //         dragHandle.style.cursor = "grab";
  //         document.removeEventListener("mousemove", onMouseMove);
  //       }, { once: true });
  //     });
  
  //     // --- 📱 Mobile Dragging (Touch) ---
  //     const onTouchMove = (e: TouchEvent) => {
  //       if (!isDragging) return;
  //       const touch = e.touches[0];
  //       requestAnimationFrame(() => moveChat(touch.clientX, touch.clientY));
  //     };
  
  //     dragHandle.addEventListener("touchstart", (e: TouchEvent) => {
  //       const touch = e.touches[0];
  
  //       isDragging = true;
  //       offsetX = touch.clientX - chatWidget.getBoundingClientRect().left;
  //       offsetY = touch.clientY - chatWidget.getBoundingClientRect().top;
  
  //       document.addEventListener("touchmove", onTouchMove);
  //       document.addEventListener("touchend", () => {
  //         isDragging = false;
  //         document.removeEventListener("touchmove", onTouchMove);
  //       }, { once: true });
  //     });
  
  //   }, 2000);
  // }
  
  toggleFrechChatDrag(chatWidget: HTMLElement, dragHandle: HTMLElement, isDragging: boolean, isRTL: boolean, initialLeft: string, initialRight: string, initialTop: string) {
    let launcher: any = document.querySelector("#fc_frame");
    if (!launcher) return;
  
    const observer = new MutationObserver(() => {
      const isOpen = chatWidget.classList.contains("fc-open");
      dragHandle.style.display = isOpen ? "none" : "block";
      isDragging = isOpen ? false : true;
  
      if (isOpen) {
        // Fix Overflow when opening
        let chatRect = chatWidget.getBoundingClientRect();
  
        // Ensure the chat is fully visible when opening
        if (chatRect.bottom > window.innerHeight) {
          chatWidget.style.top = `${window.innerHeight - chatWidget.offsetHeight}px`;
        }
        if (chatRect.right > window.innerWidth) {
          if (isRTL) {
            chatWidget.style.right = "0px";
            chatWidget.style.left = "auto";
          } else {
            chatWidget.style.left = `${window.innerWidth - chatWidget.offsetWidth}px`;
            chatWidget.style.right = "auto";
          }
        }
      } else {
        // Reset position when chat closes
        if (isRTL) {
          launcher.style.right = initialRight;
          launcher.style.left = "auto";
        } else {
          launcher.style.left = initialLeft;
          launcher.style.right = "auto";
        }
        launcher.style.top = initialTop;
      }
    });
  
    observer.observe(chatWidget, { attributes: true, attributeFilter: ["class"] });
  }
  // Freshchat-widget hide frech chat for specific roles methode
  // hideFreshChatForRoles() {
  //   const role = localStorage.getItem('loginAs');
  //   if (role === 'teacher' || role === 'supervisor') {
  //     document.body.classList.add('hide-freshchat');
  //   } else {
  //     document.body.classList.remove('hide-freshchat');
  //   }
  // }

  private initLiveHelpVisibility(): void {

    const role = localStorage.getItem('loginAs');
    const token = localStorage.getItem('token');
    if (!token || role === 'teacher' || role === 'supervisor') return;

    this.dataService.getIsGeminiVoiceWidget().subscribe(enabled => {
      this.showLiveHelp = enabled;
    });
  }

}
